import { useNavigate } from 'react-router-dom'
import { useDemoContext } from '../DemoContext'
import { formatDisplayedUsd } from '../../costDisplay'
import { Plus, Trash2, FileText, DollarSign } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDemoData } from '../useDemoData'

const formatDate = (dateStr: string, locale?: string) => {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  return date.toLocaleDateString(locale ?? 'en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export default function DemoExams() {
  const { exams, deleteExam, activeRole } = useDemoContext()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('pages')
  const { examDetails } = useDemoData()

  const totalCost = exams.reduce((sum, e) => {
    const detail = examDetails[e.id]
    return sum + (detail?.session_cost ?? 0)
  }, 0)

  const onDelete = (e: React.MouseEvent, examId: number) => {
    e.stopPropagation()
    if (!confirm(t('demo.exams.deleteConfirm'))) return
    deleteExam(examId)
  }

  return (
    <div className="animate-fade-in">
      <div className="section-header">
        <div>
          <h2 className="section-title">{t('demo.exams.title')}</h2>
          <p className="text-sm text-muted mt-4">{t('demo.exams.subtitle')}</p>
        </div>
        <div className="flex items-center gap-16">
          <div className="total-cost-inline">
            <DollarSign size={16} />
            {t('demo.exams.totalCost')}{' '}
            <span className="total-cost-value">{formatDisplayedUsd(totalCost)}</span>
          </div>
          {(activeRole === 'ORG_MANAGER' || activeRole === 'ADMIN') && (
            <button className="btn btn-primary" onClick={() => navigate('/demo/exams/new')}>
              <Plus size={16} />
              {t('demo.exams.newExam')}
            </button>
          )}
        </div>
      </div>

      {exams.length === 0 && (
        <div className="card card-padding">
          <div className="empty-state">
            <FileText />
            <h3>{t('demo.exams.emptyHeading')}</h3>
            {activeRole === 'ORG_MANAGER' || activeRole === 'ADMIN' ? (
              <>
                <p>{t('demo.exams.emptyBody')}</p>
                <button className="btn btn-primary" onClick={() => navigate('/demo/exams/new')}>
                  <Plus size={16} />
                  {t('demo.exams.emptyButton')}
                </button>
              </>
            ) : (
              <p>{t('demo.exams.teacherEmpty')}</p>
            )}
          </div>
        </div>
      )}

      {exams.length > 0 && (
        <div className="grid-4">
          {exams.map((exam) => (
            <div
              key={exam.id}
              className="card card-padding card-hoverable exam-card"
              onClick={() => navigate(`/demo/exams/${exam.id}`)}
            >
              {(activeRole === 'ORG_MANAGER' || activeRole === 'ADMIN') && (
                <button
                  className="exam-card-delete"
                  onClick={(e) => onDelete(e, exam.id)}
                  title={t('demo.exams.deleteTitle')}
                >
                  <Trash2 size={16} />
                </button>
              )}

              <div className="exam-card-header">
                <span className="text-xs text-muted">{formatDate(exam.test_date, i18n.language)}</span>
              </div>

              <div className="exam-card-title">{exam.session_name}</div>

              <div className="exam-card-stats">
                <div className="exam-card-stat">
                  <div className="exam-card-stat-value">{formatDate(exam.test_date, i18n.language)}</div>
                  <div className="exam-card-stat-label">{t('demo.exams.dateStat')}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
